// Methodology page translations. Proper nouns (BMV Global, UCS, B3, ISO 14064, TNFD, REDD+, etc.)
// intentionally left untranslated. Direct quotes are translated for the local audience while
// preserving the attribution source.

const meth = {
  en: {
    tag: "Methodology · BMV Global",
    h1: "What backs each UCS unit.",
    intro_a: "The Unidade de Crédito de Sustentabilidade (UCS) is an environmental asset born of native-vegetation conservation — quantified per hectare, registered as a financial instrument on B3, audited independently and recorded immutably on chain.",
    intro_b: "Each UCS represents",
    intro_c: "of ecosystem services.",
    pillars_label: "The three methodology pillars",
    pillar1_t: "Economic",
    pillar1_b: [
      "Proceeds from each UCS unit are channelled into ecosystem-service payments for native vegetation conservation.",
      "A portion is invested in rural properties for sustainable agriculture and in producer associations.",
      "UCS can be used as collateral, a store of value, or an instrument to meet ESG and climate commitments — booked as a commodity under IFRS.",
    ],
    pillar2_t: "Territorial",
    pillar2_b: [
      "Units originate from preserved Legal Reserves and Permanent Preservation Areas organised into voluntary conservation nuclei.",
      "Properties must demonstrate no illegal deforestation post-31 Dec 2020.",
      "Active nuclei across Amazonas, Amapá, Mato Grosso, Tocantins, Pará, Rondônia and Rio de Janeiro.",
    ],
    pillar3_t: "Environmental",
    pillar3_b: [
      "27 ecosystem-service indicators quantify each unit (1 UCS ≈ 1 tCO₂e stored).",
      "Additionality from voluntarily conserved areas above the legal minimum delivers greater climate impact.",
      "Anchored to IPCC guidance and ISO 14064 GHG accounting.",
    ],
    lc_label: "UCS lifecycle",
    lc_h: "From the property to the market.",
    lc_intro: "Every UCS unit traded on GreenBelt is traceable to a producer, an audited area, a CPR Verde number registered on B3 and an on-chain identifier. The five-step pipeline is sequential and non-reversible.",
    lc: [
      ["Area verification", "Technical eligibility analysis on the property — legal status, biome, vegetation cover."],
      ["CPR Verde issuance", "Green Rural Product Certificate registered on B3 under Brazilian Law 13.986/2020 and Decree 10.828/2021."],
      ["Independent audit", "On-site studies and verification by SGS, ERM, TÜV SÜD, DNV or equivalent international auditors."],
      ["Blockchain registry", "Immutable on-chain record on the BMV Digital platform — solves the double-spending problem."],
      ["Market offering", "Listed on GreenBelt and accredited venues. Financial return flows to the producer."],
    ],
    reg_label: "Regulators & verifiers",
    reg: [
      ["Banco Central do Brasil", "Recognised the UCS model through the LIFT initiative as strategic innovation."],
      ["CVM (Securities Commission)", "Authorises financial market infrastructure participants."],
      ["B3 (Brazilian Stock Exchange)", "Registers the CPR Verde as an exchange-traded financial instrument."],
      ["ERM", "Issued the Second-Party Opinion validating the methodology."],
      ["SGS · DNV · TÜV SÜD", "Independent verification and field audits per nucleus."],
    ],
    std_label: "Aligned with",
    adc_label: "Anti double-counting",
    adc_h: "No unit can be claimed twice.",
    adc_b: [
      "Measurement, Reporting & Verification (MRV) methodology eliminates overlap risk.",
      "Blockchain registry on the BMV Digital platform provides a public, auditable trail.",
      "Retirement on GreenBelt is one-way and produces a verifiable certificate (id starts with GB-).",
    ],
    gov_label: "Governance",
    gov_h: "Compliance committee + ethics channel.",
    gov_b: [
      "Compliance Committee reviews every project entry before listing.",
      "Strict AML / KYC: producers are background-checked for legal encumbrances and environmental infractions.",
      "Independent audit committee oversight via internationally recognised certifiers.",
      "GreenBelt corporate KYB enforces FinCEN CDD, EU 5/6 AMLD and GCC FATF guidance on the buyer side.",
    ],
    q_label: "In their own words",
    quotes: [
      ["A strategic innovation that combines environmental preservation, financial inclusion and the modernisation of the national financial system.", "Banco Central do Brasil · LIFT recognition"],
      ["UCS are commodities which can be acquired and evidenced under IFRS — as a store of value, for asset appreciation, or as instruments for fulfilling environmental, social and climate commitments.", "BMV Group · SPO 2025"],
      ["The integration of decentralised finance with traditional regulation — blockchain liquidity pools, smart contracts, decentralised oracles and multi-layer verification.", "BMV Global · Methodology brief"],
    ],
    src_label: "Source documents",
    src_h: "Read the original methodology.",
    src_open: "Open →",
    src_primary: "Primary",
    cta_label: "Ready to trade",
    cta_h: "Verified provenance. Real-time pricing. Multi-currency settlement.",
    cta_market: "Explore marketplace →",
    cta_register: "Open account",
  },

  pt: {
    tag: "Metodologia · BMV Global",
    h1: "O que lastreia cada unidade UCS.",
    intro_a: "A Unidade de Crédito de Sustentabilidade (UCS) é um ativo ambiental originado da conservação de vegetação nativa — quantificado por hectare, registrado como instrumento financeiro na B3, auditado de forma independente e gravado de modo imutável em blockchain.",
    intro_b: "Cada UCS representa",
    intro_c: "de serviços ecossistêmicos.",
    pillars_label: "Os três pilares da metodologia",
    pillar1_t: "Econômico",
    pillar1_b: [
      "Os proventos de cada UCS são direcionados a pagamentos por serviços ecossistêmicos para conservação de vegetação nativa.",
      "Parte é investida em propriedades rurais para agricultura sustentável e em associações de produtores.",
      "A UCS pode ser usada como garantia, reserva de valor ou instrumento para cumprir compromissos ESG e climáticos — contabilizada como commodity sob IFRS.",
    ],
    pillar2_t: "Territorial",
    pillar2_b: [
      "As unidades originam-se de Reservas Legais e Áreas de Preservação Permanente organizadas em núcleos voluntários de conservação.",
      "As propriedades devem comprovar ausência de desmatamento ilegal após 31/12/2020.",
      "Núcleos ativos no Amazonas, Amapá, Mato Grosso, Tocantins, Pará, Rondônia e Rio de Janeiro.",
    ],
    pillar3_t: "Ambiental",
    pillar3_b: [
      "27 indicadores de serviços ecossistêmicos quantificam cada unidade (1 UCS ≈ 1 tCO₂e armazenada).",
      "A adicionalidade de áreas voluntariamente conservadas acima do mínimo legal entrega maior impacto climático.",
      "Ancorada nas diretrizes do IPCC e na contabilização de GEE da ISO 14064.",
    ],
    lc_label: "Ciclo de vida da UCS",
    lc_h: "Da propriedade ao mercado.",
    lc_intro: "Cada UCS negociada na GreenBelt é rastreável até um produtor, uma área auditada, um número de CPR Verde registrado na B3 e um identificador em blockchain. O pipeline de cinco etapas é sequencial e irreversível.",
    lc: [
      ["Verificação da área", "Análise técnica de elegibilidade da propriedade — situação jurídica, bioma, cobertura vegetal."],
      ["Emissão da CPR Verde", "Cédula de Produto Rural Verde registrada na B3 sob a Lei 13.986/2020 e o Decreto 10.828/2021."],
      ["Auditoria independente", "Estudos de campo e verificação por SGS, ERM, TÜV SÜD, DNV ou auditores internacionais equivalentes."],
      ["Registro em blockchain", "Registro imutável na plataforma BMV Digital — resolve o problema do gasto duplo."],
      ["Oferta ao mercado", "Listada na GreenBelt e em plataformas credenciadas. O retorno financeiro flui para o produtor."],
    ],
    reg_label: "Reguladores e verificadores",
    reg: [
      ["Banco Central do Brasil", "Reconheceu o modelo UCS por meio da iniciativa LIFT como inovação estratégica."],
      ["CVM (Comissão de Valores Mobiliários)", "Autoriza participantes de infraestrutura do mercado financeiro."],
      ["B3 (Bolsa de Valores)", "Registra a CPR Verde como instrumento financeiro negociado em bolsa."],
      ["ERM", "Emitiu a Second-Party Opinion validando a metodologia."],
      ["SGS · DNV · TÜV SÜD", "Verificação independente e auditorias de campo por núcleo."],
    ],
    std_label: "Alinhada com",
    adc_label: "Antidupla contagem",
    adc_h: "Nenhuma unidade pode ser reivindicada duas vezes.",
    adc_b: [
      "A metodologia MRV (Medição, Relato e Verificação) elimina o risco de sobreposição.",
      "O registro em blockchain na plataforma BMV Digital oferece trilha pública e auditável.",
      "A retirada (aposentadoria) na GreenBelt é unidirecional e gera um certificado verificável (id começa com GB-).",
    ],
    gov_label: "Governança",
    gov_h: "Comitê de compliance + canal de ética.",
    gov_b: [
      "O Comitê de Compliance revisa todo cadastro de projeto antes da listagem.",
      "AML / KYC rigorosos: produtores passam por background check de ônus jurídicos e infrações ambientais.",
      "Supervisão independente do comitê de auditoria via certificadoras internacionalmente reconhecidas.",
      "O KYB corporativo da GreenBelt aplica FinCEN CDD, 5ª/6ª AMLD da UE e diretrizes FATF do CCG no lado do comprador.",
    ],
    q_label: "Nas próprias palavras",
    quotes: [
      ["Uma inovação estratégica que combina preservação ambiental, inclusão financeira e modernização do sistema financeiro nacional.", "Banco Central do Brasil · reconhecimento LIFT"],
      ["A UCS é uma commodity que pode ser adquirida e evidenciada sob IFRS — como reserva de valor, para valorização patrimonial ou como instrumento para cumprir compromissos ambientais, sociais e climáticos.", "Grupo BMV · SPO 2025"],
      ["A integração das finanças descentralizadas com a regulação tradicional — pools de liquidez em blockchain, contratos inteligentes, oráculos descentralizados e verificação em múltiplas camadas.", "BMV Global · Resumo da metodologia"],
    ],
    src_label: "Documentos-fonte",
    src_h: "Leia a metodologia original.",
    src_open: "Abrir →",
    src_primary: "Recomendado para leitores em português",
    cta_label: "Pronto para negociar",
    cta_h: "Proveniência verificada. Preço em tempo real. Liquidação multi-moeda.",
    cta_market: "Explorar marketplace →",
    cta_register: "Abrir conta",
  },

  es: {
    tag: "Metodología · BMV Global",
    h1: "Qué respalda cada unidad UCS.",
    intro_a: "La Unidade de Crédito de Sustentabilidade (UCS) es un activo ambiental nacido de la conservación de vegetación nativa — cuantificado por hectárea, registrado como instrumento financiero en B3, auditado de forma independiente y registrado de manera inmutable en blockchain.",
    intro_b: "Cada UCS representa",
    intro_c: "de servicios ecosistémicos.",
    pillars_label: "Los tres pilares de la metodología",
    pillar1_t: "Económico",
    pillar1_b: [
      "Los ingresos de cada UCS se destinan a pagos por servicios ecosistémicos para la conservación de la vegetación nativa.",
      "Una parte se invierte en propiedades rurales para agricultura sostenible y en asociaciones de productores.",
      "La UCS puede usarse como colateral, depósito de valor o instrumento para cumplir compromisos ESG y climáticos — contabilizada como commodity bajo IFRS.",
    ],
    pillar2_t: "Territorial",
    pillar2_b: [
      "Las unidades se originan en Reservas Legales y Áreas de Preservación Permanente organizadas en núcleos voluntarios de conservación.",
      "Las propiedades deben demostrar la ausencia de deforestación ilegal posterior al 31/12/2020.",
      "Núcleos activos en Amazonas, Amapá, Mato Grosso, Tocantins, Pará, Rondônia y Río de Janeiro.",
    ],
    pillar3_t: "Ambiental",
    pillar3_b: [
      "27 indicadores de servicios ecosistémicos cuantifican cada unidad (1 UCS ≈ 1 tCO₂e almacenada).",
      "La adicionalidad de áreas conservadas voluntariamente más allá del mínimo legal entrega mayor impacto climático.",
      "Anclada a las directrices del IPCC y a la contabilización de GEI de ISO 14064.",
    ],
    lc_label: "Ciclo de vida de la UCS",
    lc_h: "De la propiedad al mercado.",
    lc_intro: "Cada UCS negociada en GreenBelt es trazable hasta un productor, un área auditada, un número de CPR Verde registrado en B3 y un identificador on-chain. El pipeline de cinco etapas es secuencial e irreversible.",
    lc: [
      ["Verificación del área", "Análisis técnico de elegibilidad de la propiedad — estado jurídico, bioma, cobertura vegetal."],
      ["Emisión de CPR Verde", "Cédula de Producto Rural Verde registrada en B3 bajo la Ley 13.986/2020 y el Decreto 10.828/2021."],
      ["Auditoría independiente", "Estudios de campo y verificación por SGS, ERM, TÜV SÜD, DNV o auditores internacionales equivalentes."],
      ["Registro en blockchain", "Registro inmutable en la plataforma BMV Digital — resuelve el problema del doble gasto."],
      ["Oferta al mercado", "Listada en GreenBelt y en plataformas acreditadas. El retorno financiero fluye al productor."],
    ],
    reg_label: "Reguladores y verificadores",
    reg: [
      ["Banco Central de Brasil", "Reconoció el modelo UCS mediante la iniciativa LIFT como innovación estratégica."],
      ["CVM (Comisión de Valores)", "Autoriza a los participantes de la infraestructura del mercado financiero."],
      ["B3 (Bolsa de Brasil)", "Registra la CPR Verde como instrumento financiero negociado en bolsa."],
      ["ERM", "Emitió la Second-Party Opinion validando la metodología."],
      ["SGS · DNV · TÜV SÜD", "Verificación independiente y auditorías de campo por núcleo."],
    ],
    std_label: "Alineada con",
    adc_label: "Anti doble contabilización",
    adc_h: "Ninguna unidad puede reclamarse dos veces.",
    adc_b: [
      "La metodología MRV (Medición, Reporte y Verificación) elimina el riesgo de solapamiento.",
      "El registro en blockchain en la plataforma BMV Digital ofrece una traza pública y auditable.",
      "La retirada en GreenBelt es unidireccional y produce un certificado verificable (id comienza con GB-).",
    ],
    gov_label: "Gobernanza",
    gov_h: "Comité de compliance + canal de ética.",
    gov_b: [
      "El Comité de Compliance revisa cada registro de proyecto antes de su listado.",
      "AML / KYC estrictos: los productores se someten a verificación de antecedentes por cargas legales e infracciones ambientales.",
      "Supervisión independiente del comité de auditoría mediante certificadoras reconocidas internacionalmente.",
      "El KYB corporativo de GreenBelt aplica FinCEN CDD, 5ª/6ª AMLD de la UE y guía FATF del CCG del lado del comprador.",
    ],
    q_label: "En sus propias palabras",
    quotes: [
      ["Una innovación estratégica que combina la preservación ambiental, la inclusión financiera y la modernización del sistema financiero nacional.", "Banco Central de Brasil · reconocimiento LIFT"],
      ["La UCS es una commodity que puede adquirirse y evidenciarse bajo IFRS — como depósito de valor, para apreciación de activos o como instrumento para cumplir compromisos ambientales, sociales y climáticos.", "Grupo BMV · SPO 2025"],
      ["La integración de las finanzas descentralizadas con la regulación tradicional — pools de liquidez en blockchain, contratos inteligentes, oráculos descentralizados y verificación multicapa.", "BMV Global · Resumen metodológico"],
    ],
    src_label: "Documentos fuente",
    src_h: "Lea la metodología original.",
    src_open: "Abrir →",
    src_primary: "Recomendado para lectores hispanohablantes",
    cta_label: "Listo para operar",
    cta_h: "Procedencia verificada. Precios en tiempo real. Liquidación multi-moneda.",
    cta_market: "Explorar marketplace →",
    cta_register: "Abrir cuenta",
  },

  fr: {
    tag: "Méthodologie · BMV Global",
    h1: "Ce qui adosse chaque unité UCS.",
    intro_a: "L'Unidade de Crédito de Sustentabilidade (UCS) est un actif environnemental issu de la conservation de la végétation indigène — quantifié par hectare, enregistré comme instrument financier sur B3, audité de manière indépendante et inscrit de façon immuable sur blockchain.",
    intro_b: "Chaque UCS représente",
    intro_c: "de services écosystémiques.",
    pillars_label: "Les trois piliers de la méthodologie",
    pillar1_t: "Économique",
    pillar1_b: [
      "Les produits de chaque UCS financent les paiements pour services écosystémiques de la conservation de la végétation indigène.",
      "Une part est investie dans des propriétés rurales pour l'agriculture durable et dans des associations de producteurs.",
      "L'UCS peut servir de collatéral, de réserve de valeur ou d'instrument pour honorer des engagements ESG et climatiques — comptabilisée comme commodité selon les IFRS.",
    ],
    pillar2_t: "Territorial",
    pillar2_b: [
      "Les unités proviennent de Réserves Légales et Aires de Préservation Permanente organisées en noyaux volontaires de conservation.",
      "Les propriétés doivent prouver l'absence de déforestation illégale après le 31/12/2020.",
      "Noyaux actifs en Amazonas, Amapá, Mato Grosso, Tocantins, Pará, Rondônia et Rio de Janeiro.",
    ],
    pillar3_t: "Environnemental",
    pillar3_b: [
      "27 indicateurs de services écosystémiques quantifient chaque unité (1 UCS ≈ 1 tCO₂e stockée).",
      "L'additionnalité issue des zones conservées au-delà du minimum légal apporte un impact climatique supérieur.",
      "Ancrée aux lignes directrices du GIEC et à la comptabilisation des GES ISO 14064.",
    ],
    lc_label: "Cycle de vie de l'UCS",
    lc_h: "De la propriété au marché.",
    lc_intro: "Chaque UCS échangée sur GreenBelt est traçable jusqu'à un producteur, une aire auditée, un numéro de CPR Verde enregistré sur B3 et un identifiant on-chain. Le pipeline en cinq étapes est séquentiel et non réversible.",
    lc: [
      ["Vérification de l'aire", "Analyse technique d'éligibilité de la propriété — statut juridique, biome, couverture végétale."],
      ["Émission de CPR Verde", "Cédula de Produto Rural Verte enregistrée sur B3 selon la Loi 13.986/2020 et le Décret 10.828/2021."],
      ["Audit indépendant", "Études de terrain et vérification par SGS, ERM, TÜV SÜD, DNV ou auditeurs internationaux équivalents."],
      ["Registre blockchain", "Enregistrement immuable sur la plateforme BMV Digital — résout le problème de la double dépense."],
      ["Mise en marché", "Listée sur GreenBelt et plateformes accréditées. Le retour financier revient au producteur."],
    ],
    reg_label: "Régulateurs et vérificateurs",
    reg: [
      ["Banco Central do Brasil", "A reconnu le modèle UCS via l'initiative LIFT comme innovation stratégique."],
      ["CVM (Commission des valeurs mobilières)", "Autorise les participants de l'infrastructure du marché financier."],
      ["B3 (Bourse brésilienne)", "Enregistre la CPR Verde comme instrument financier négocié en bourse."],
      ["ERM", "A émis la Second-Party Opinion validant la méthodologie."],
      ["SGS · DNV · TÜV SÜD", "Vérification indépendante et audits de terrain par noyau."],
    ],
    std_label: "Alignée avec",
    adc_label: "Anti double comptage",
    adc_h: "Aucune unité ne peut être revendiquée deux fois.",
    adc_b: [
      "La méthodologie MRV (Mesure, Rapport et Vérification) élimine le risque de chevauchement.",
      "Le registre blockchain sur la plateforme BMV Digital fournit une piste publique et auditable.",
      "Le retrait sur GreenBelt est unidirectionnel et produit un certificat vérifiable (id commence par GB-).",
    ],
    gov_label: "Gouvernance",
    gov_h: "Comité de conformité + canal éthique.",
    gov_b: [
      "Le Comité de Conformité examine chaque entrée de projet avant la mise en ligne.",
      "AML / KYC stricts : les producteurs subissent un contrôle d'antécédents pour charges juridiques et infractions environnementales.",
      "Supervision indépendante du comité d'audit via des certificateurs internationalement reconnus.",
      "Le KYB d'entreprise de GreenBelt applique FinCEN CDD, 5e/6e AMLD de l'UE et lignes directrices FATF du CCG du côté acheteur.",
    ],
    q_label: "Dans leurs propres mots",
    quotes: [
      ["Une innovation stratégique qui combine préservation environnementale, inclusion financière et modernisation du système financier national.", "Banco Central do Brasil · reconnaissance LIFT"],
      ["L'UCS est une commodité qui peut être acquise et attestée selon les IFRS — comme réserve de valeur, pour appréciation d'actifs ou comme instrument pour honorer des engagements environnementaux, sociaux et climatiques.", "Groupe BMV · SPO 2025"],
      ["L'intégration de la finance décentralisée avec la régulation traditionnelle — pools de liquidité en blockchain, contrats intelligents, oracles décentralisés et vérification multi-couches.", "BMV Global · Résumé méthodologique"],
    ],
    src_label: "Documents sources",
    src_h: "Lire la méthodologie originale.",
    src_open: "Ouvrir →",
    src_primary: "Recommandé pour les lecteurs francophones",
    cta_label: "Prêt à trader",
    cta_h: "Provenance vérifiée. Tarification en temps réel. Règlement multi-devises.",
    cta_market: "Explorer le marketplace →",
    cta_register: "Ouvrir un compte",
  },

  ar: {
    tag: "المنهجية · BMV Global",
    h1: "ما يدعم كل وحدة UCS.",
    intro_a: "وحدة ائتمان الاستدامة (UCS) هي أصل بيئي ناشئ عن حفظ الغطاء النباتي الأصلي — مُقيَّم لكل هكتار، مُسجَّل كأداة مالية في بورصة B3، مُدقَّق بشكل مستقل ومُسجَّل بشكل دائم على البلوكشين.",
    intro_b: "كل UCS يمثل",
    intro_c: "من خدمات النظام البيئي.",
    pillars_label: "ركائز المنهجية الثلاث",
    pillar1_t: "اقتصادي",
    pillar1_b: [
      "تُوجَّه عائدات كل UCS إلى مدفوعات خدمات النظام البيئي لحفظ الغطاء النباتي الأصلي.",
      "يُستثمر جزء منها في عقارات ريفية للزراعة المستدامة وفي جمعيات المنتجين.",
      "يمكن استخدام UCS كضمان، أو مخزن للقيمة، أو أداة للوفاء بالالتزامات البيئية والاجتماعية والمناخية — تُحتسب كسلعة بموجب IFRS.",
    ],
    pillar2_t: "إقليمي",
    pillar2_b: [
      "تنشأ الوحدات من المحميات القانونية ومناطق الحفظ الدائم المنظَّمة في نوى حفظ تطوعية.",
      "يجب أن تُثبت العقارات عدم وجود إزالة غابات غير مشروعة بعد 31/12/2020.",
      "نوى نشطة في أمازوناس وأمابا وماتو غروسو وتوكانتينس وبارا ورندونيا وريو دي جانيرو.",
    ],
    pillar3_t: "بيئي",
    pillar3_b: [
      "27 مؤشرًا لخدمات النظام البيئي تُقدِّر كل وحدة (1 UCS ≈ 1 طن CO₂ مكافئ مُخزَّن).",
      "الإضافية الناتجة عن المناطق المحفوظة تطوعيًا فوق الحد الأدنى القانوني تحقق أثرًا مناخيًا أكبر.",
      "مرتكزة على إرشادات الهيئة الحكومية الدولية المعنية بتغير المناخ (IPCC) ومحاسبة الغازات الدفيئة وفق ISO 14064.",
    ],
    lc_label: "دورة حياة UCS",
    lc_h: "من العقار إلى السوق.",
    lc_intro: "كل UCS يُتداول على GreenBelt قابل للتتبع إلى منتج، ومنطقة مدققة، ورقم CPR Verde مسجل في B3، ومُعرِّف على البلوكشين. خط الأنابيب من خمس مراحل تسلسلي وغير قابل للعكس.",
    lc: [
      ["التحقق من المنطقة", "تحليل تقني لأهلية العقار — الوضع القانوني، النظام البيئي الحيوي، الغطاء النباتي."],
      ["إصدار CPR Verde", "شهادة المنتج الريفي الأخضر مسجلة في B3 بموجب القانون البرازيلي 13.986/2020 والمرسوم 10.828/2021."],
      ["تدقيق مستقل", "دراسات ميدانية وتحقق من قِبل SGS وERM وTÜV SÜD وDNV أو مدققين دوليين معادلين."],
      ["سجل البلوكشين", "تسجيل دائم على منصة BMV Digital — يحل مشكلة الإنفاق المزدوج."],
      ["الطرح في السوق", "مدرجة في GreenBelt والمنصات المعتمدة. العائد المالي يعود إلى المنتج."],
    ],
    reg_label: "الجهات التنظيمية والمدققون",
    reg: [
      ["البنك المركزي البرازيلي", "اعترف بنموذج UCS من خلال مبادرة LIFT كابتكار استراتيجي."],
      ["CVM (هيئة الأوراق المالية)", "تُرخِّص المشاركين في البنية التحتية للسوق المالي."],
      ["B3 (البورصة البرازيلية)", "تُسجِّل CPR Verde كأداة مالية متداولة في البورصة."],
      ["ERM", "أصدرت تقرير الرأي المستقل المُصادِق على المنهجية."],
      ["SGS · DNV · TÜV SÜD", "تحقق مستقل وعمليات تدقيق ميدانية لكل نواة."],
    ],
    std_label: "متوافقة مع",
    adc_label: "منع الاحتساب المزدوج",
    adc_h: "لا يمكن المطالبة بأي وحدة مرتين.",
    adc_b: [
      "منهجية القياس والإبلاغ والتحقق (MRV) تستبعد خطر التداخل.",
      "سجل البلوكشين على منصة BMV Digital يوفر مسارًا عامًا قابلًا للتدقيق.",
      "الإحالة إلى التقاعد على GreenBelt في اتجاه واحد وتُنتج شهادة قابلة للتحقق (يبدأ المعرف بـ GB-).",
    ],
    gov_label: "الحوكمة",
    gov_h: "لجنة الامتثال + قناة الأخلاقيات.",
    gov_b: [
      "تراجع لجنة الامتثال كل ملف مشروع قبل الإدراج.",
      "AML / KYC صارمان: يخضع المنتجون لفحص خلفية بشأن الأعباء القانونية والمخالفات البيئية.",
      "إشراف لجنة تدقيق مستقلة عبر شهادات معترف بها دوليًا.",
      "KYB الشركات في GreenBelt يطبق FinCEN CDD، AMLD الخامس/السادس للاتحاد الأوروبي، وإرشادات FATF لمجلس التعاون الخليجي من جانب المشتري.",
    ],
    q_label: "بكلماتهم",
    quotes: [
      ["ابتكار استراتيجي يجمع بين الحفاظ على البيئة والشمول المالي وتحديث النظام المالي الوطني.", "البنك المركزي البرازيلي · اعتراف LIFT"],
      ["UCS سلعة يمكن اقتناؤها وإثباتها وفق IFRS — كمخزن للقيمة، لتقدير الأصول، أو كأداة للوفاء بالالتزامات البيئية والاجتماعية والمناخية.", "مجموعة BMV · SPO 2025"],
      ["تكامل التمويل اللامركزي مع التنظيم التقليدي — برك سيولة على البلوكشين، عقود ذكية، أوراكلز لامركزية، وتحقق متعدد الطبقات.", "BMV Global · موجز المنهجية"],
    ],
    src_label: "وثائق المصدر",
    src_h: "اقرأ المنهجية الأصلية.",
    src_open: "فتح ←",
    src_primary: "موصى به للقراء بالعربية",
    cta_label: "جاهز للتداول",
    cta_h: "منشأ موثق. تسعير لحظي. تسوية متعددة العملات.",
    cta_market: "استكشف السوق ←",
    cta_register: "افتح حسابًا",
  },
};

export default meth;
